import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { opsApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { CostOverview } from '../../types';
import { errorMessage } from '../../utils/errors';
import SectionTabs, { SectionTab } from '../../components/admin/SectionTabs';
import AiCosts from '../../components/costs/AiCosts';
import AwsCosts from '../../components/costs/AwsCosts';
import CostSummary from '../../components/costs/CostSummary';
import DatabaseCosts from '../../components/costs/DatabaseCosts';
import ServiceCosts from '../../components/costs/ServiceCosts';

const TABS: SectionTab[] = [
  { value: 'summary', label: 'Summary' },
  { value: 'aws', label: 'AWS' },
  { value: 'database', label: 'Database' },
  { value: 'ai', label: 'AI and tailored resumes' },
  { value: 'services', label: 'Other services' },
];

/**
 * The Costs section of the staff area: what JobFlo costs to run, from the AWS
 * bill, the database provider, our own record of paid model calls and the
 * costs staff enter by hand.
 */
const CostsPage: React.FC = () => {
  const { tab: tabParam } = useParams();
  const tab = TABS.some((t) => t.value === tabParam) ? (tabParam as string) : 'summary';
  const { show, toast } = useToast();
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Only the newest answer is kept, so overlapping reloads cannot go backwards.
  const requestId = useRef(0);
  const load = useCallback(async (refresh = false) => {
    const id = ++requestId.current;
    if (refresh) setRefreshing(true);
    try {
      const data = await opsApi.costs(refresh);
      if (id !== requestId.current) return;
      setOverview(data);
      setError(null);
    } catch (e) {
      if (id !== requestId.current) return;
      setError(errorMessage(e, 'Could not load the costs. Please try again.'));
    } finally {
      if (id === requestId.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  return (
    <Box>
      <SectionTabs label="Costs" tabs={TABS} value={tab} to={(value) => `/admin/costs/${value}`} dense />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}>
          {overview ? `The numbers below may be out of date. ${error}` : error}
        </Alert>
      )}

      {!overview ? (
        !error && (
          <Stack spacing={1.5} aria-busy="true" aria-label="Loading costs">
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={220} />
          </Stack>
        )
      ) : (
        <Stack spacing={2}>
          {tab === 'summary' && <CostSummary overview={overview} />}
          {tab === 'aws' && <AwsCosts aws={overview.aws} />}
          {tab === 'database' && <DatabaseCosts database={overview.database} />}
          {tab === 'ai' && <AiCosts ai={overview.ai} />}
          {tab === 'services' && <ServiceCosts summary={overview.services} onChanged={() => load()} notify={show} />}

          {(tab === 'summary' || tab === 'aws' || tab === 'database') && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={refreshing} onClick={() => load(true)}>
                {refreshing ? 'Reading...' : 'Read AWS and the database again'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                AWS figures are kept for a day, because AWS charges a few cents each time its bill is read.
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {toast}
    </Box>
  );
};

export default CostsPage;
